import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from '@fluentui/react-components';
import { BranchCompareRegular, CheckmarkFilled, Dismiss24Regular } from '@fluentui/react-icons';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ControlledField, OptionSwatch, ButtonLoading, TConfirmDialogState, ConfirmDialog } from '@shared/components';
import { changeNoteSchema } from '@shared/schemas/changeNote.schema';

import { TFile, TFileVersion, useCreateVersion } from '@modules/files';
import { isTDocument, TDocument, TVersion, updateCompleteQueryData, useComplete } from '@modules/documents';

import config from '@config/index';

import getValidVersionIds from '../../helpers/getValidVersionIds.helper';
import { TCreateVersionFormData, TResolvedItemsMap } from '../../types/items.types';
import { TPermissions } from '@modules/access/types';
import splitExtension from '../../helpers/splitExtension.helper';
import useItemScreenStore, { EItemScreenMode } from '../../stores/useItemScreenStore';
import UnresolvedCommentsWarning from '@modules/comments/components/UnresolvedCommentsWarning';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from '@shared/hooks';
import { TInlineBlock, useCompleteInlineBlock } from '@modules/inlineBlocks';
import { SignInContext } from '@modules/users';
import BlockCompleteWarning from './components/BlockCompleteWarning';
import PdfReviewWarning from './components/PdfReviewWarning';
import TemplateUsageWarning from './components/TemplateUsageWarning';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  item: TDocument | TFile | TInlineBlock;
  resolvedItems: TResolvedItemsMap;

  // --- document specific
  permissions?: TPermissions;
  completeWhileEditing?: (request: (editingDocumentId?: string) => Promise<TVersion>) => Promise<TVersion>;
  disconnect?: () => void;
};

const getPendingChangeNoteSummary = (versions: TVersion[] | TFileVersion[], approvedVersionId?: string) => {
  const approvedVersionIndex = approvedVersionId
    ? versions.findIndex((version) => version.id === approvedVersionId)
    : -1;
  const pendingVersions = approvedVersionIndex >= 0 ? versions.slice(0, approvedVersionIndex) : versions;

  return {
    hasPendingVersions: pendingVersions.length > 0,
    combinedNote: pendingVersions
      .slice()
      .reverse()
      .map((version) => version.note)
      .filter((note) => note.trim().length > 0)
      .join('\n\n'),
  };
};

const useStyles = makeStyles({
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

const CreateVersionDialog = ({
  isOpen,
  setIsOpen,
  item,
  resolvedItems,
  permissions,
  completeWhileEditing,
  disconnect,
}: TProps) => {
  const { organization } = useContext(SignInContext);
  const complete = useComplete();
  const createVersion = useCreateVersion();
  const completeInlineBlock = useCompleteInlineBlock();

  const queryClient = useQueryClient();
  const { change } = useSearchParams();

  const isDoc = isTDocument(item);
  const isContentBlock = isDoc && item.isBlock;

  /**
   * `onSubmit` was previously in the `DocumentContext` which was causing unnecessary re-renders of this component.
   * The `isDoc` check is not ideal, but it's a workaround which slighly improves performance.
   */
  const onSubmit = useCallback(
    async (data: TCreateVersionFormData, versionId: string) => {
      try {
        let newVersion = null;

        switch (item.type) {
          case 'DOCUMENT':
            if (!completeWhileEditing) throw new Error('completeWhileEditing is not defined for complete draft!');
            newVersion = await completeWhileEditing((editingDocumentId) =>
              complete.mutateAsync({ id: item._id, note: data.note, versionId, isEditing: Boolean(editingDocumentId) })
            );
            updateCompleteQueryData(queryClient, item._id, newVersion);
            break;
          case 'FILE':
            if (!data.file) return false;
            await createVersion.mutateAsync({ id: item._id, jsFile: data.file[0], note: data.note, versionId });
            change('view');
            break;
          case 'INLINE_BLOCK':
            newVersion = await completeInlineBlock.mutateAsync(
              { id: item._id, version: versionId, note: data.note },
              { onSuccess: disconnect }
            );
            break;
          default:
            throw new Error(`Unsupported item type: ${(item as any).type}`);
        }

        return true;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    [item?._id, completeWhileEditing]
  );
  const schema = useMemo(
    () =>
      yup.object({
        note: changeNoteSchema,
        file:
          item.type !== 'FILE'
            ? yup.mixed<FileList>().nullable()
            : yup
                .mixed<FileList>()
                .required('File is required.')
                .test(
                  'size',
                  `File exceeds our file size limit of ${config.MAX_FILE_SIZES_MB.IMPORT_FILE}MB!`,
                  (value) => value && value[0].size <= config.MAX_FILE_SIZES_MB.IMPORT_FILE * 1024 * 1024
                ),
      }),
    [item.type]
  );

  const handleFormSubmit = async (formData: TCreateVersionFormData) => {
    setIsLoading(true);
    const success = await onSubmit(formData, selectedVersionId);
    setIsLoading(false);

    if (!success) return;

    setIsOpen(false);
    setTimeout(reset, 300);

    // disabled for now: if (isDoc && !item.isBlock && organization.featureSettings.impactAnalysis.enabled)
    // openImpactDialog();
  };

  const openImpactDialog = () => {
    const newConfirmDialogState: TConfirmDialogState = {
      isOpen: true,
      title: 'Analyze impacts of the file change?',
      content:
        'The changes made in this version might require other files to be updated as well. Would you like to analyze the potential impacts of this change?',
      buttonLabel: 'Analyze impacts',
      buttonIcon: <BranchCompareRegular />,
      secondaryButtonLabel: 'Skip / later',
      onConfirm: () => {
        setScreenMode(EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS);
        setConfirmDialogState({ ...newConfirmDialogState, isOpen: false });
      },
    };
    setConfirmDialogState(newConfirmDialogState);
  };

  type FormData = yup.InferType<typeof schema>;

  const [validVersionIds, setValidVersionIds] = useState(getValidVersionIds(item));
  const [selectedVersionId, setSelectedVersionId] = useState(validVersionIds[validVersionIds.length - 1]);

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { note: organization.versioningConfig?.changeNoteDefault || '', file: null },
  });

  const noteValue = useWatch({ control, name: 'note' }) ?? '';
  const refNote = useRef<HTMLTextAreaElement>(null);

  const supportsPreviousNotes = item.type === 'FILE' || (isDoc && !item.isBlock);
  const previousNoteSummary = useMemo(
    () =>
      supportsPreviousNotes
        ? getPendingChangeNoteSummary(item.versions as TVersion[] | TFileVersion[], item.stats.approvedVersionId)
        : { hasPendingVersions: false, combinedNote: '' },
    [supportsPreviousNotes, item.versions, item.stats.approvedVersionId]
  );
  const showPreviousNotesButton =
    supportsPreviousNotes && previousNoteSummary.hasPendingVersions && noteValue.trim().length === 0;

  const handleUsePreviousNotes = useCallback(() => {
    setValue('note', previousNoteSummary.combinedNote, { shouldDirty: true, shouldValidate: true });

    requestAnimationFrame(() => {
      const el = refNote.current;
      if (!el) return;

      el.focus({ preventScroll: true });

      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        // do nothing
      }
    });
  }, [previousNoteSummary.combinedNote, setValue]);

  useEffect(() => {
    if (!isOpen) return;

    // refresh version options when dialog opens
    const versionIds = getValidVersionIds(item);
    setValidVersionIds(versionIds);
    setSelectedVersionId(versionIds[versionIds.length - 1]);

    // focus & move caret to end
    const el = refNote.current;
    if (!el) return;

    el.focus({ preventScroll: true });

    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch {
      // do nothing
    }
  }, [isOpen]);

  const getAccept = (file: TFile) => {
    const [_, ext] = splitExtension(file.name);
    return ext ? `.${ext}` : undefined;
  };

  const { setScreenMode } = useItemScreenStore();
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const showBlockWarning = (isDoc && item.isBlock) || item.type === 'INLINE_BLOCK';
  const showTemplateWarning = isDoc && item.isTemplate;

  const classes = useStyles();
  return (
    <>
      <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />

      <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
        <DialogSurface>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <DialogBody>
              <DialogTitle
                action={
                  <DialogTrigger action="close">
                    <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                  </DialogTrigger>
                }
              >
                {isDoc ? 'Complete draft and create version' : 'Create new version'}{' '}
                {validVersionIds[validVersionIds.length - 1]}
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <div className="space-y-2 mb-4">
                  {showTemplateWarning && <TemplateUsageWarning doc={item} />}
                  {showBlockWarning && <BlockCompleteWarning block={item} resolvedItems={resolvedItems} />}
                  <UnresolvedCommentsWarning item={item} setIsDialogOpen={setIsOpen} />
                  {isDoc && !isContentBlock && permissions && <PdfReviewWarning doc={item} permissions={permissions} />}
                </div>

                <div className="relative">
                  <ControlledField
                    label="Change note"
                    name="note"
                    control={control}
                    errors={errors}
                    required
                    inputType="textarea"
                    placeholder="Briefly describe what changed."
                    ref={refNote}
                    className="h-28"
                  />
                  {showPreviousNotesButton && (
                    <Button
                      type="button"
                      size="small"
                      appearance="secondary"
                      className={`absolute left-3 z-10 ${errors.note ? 'bottom-8' : 'bottom-2'}`}
                      onClick={handleUsePreviousNotes}
                    >
                      Use previous notes
                    </Button>
                  )}
                </div>
                {false && (
                  <OptionSwatch
                    label="Select version number:"
                    selectedValue={selectedVersionId}
                    setSelectedValue={setSelectedVersionId}
                    options={validVersionIds.map((versionId, idx) => ({
                      value: versionId,
                      title: `Version ${versionId}`,
                      subtitle: ['Major', 'Minor', 'Patch'][idx] + ' update',
                    }))}
                    className="my-4"
                  />
                )}
                {item.type === 'FILE' && (
                  <div className="my-4">
                    <ControlledField
                      inputType="file"
                      label="New file"
                      name="file"
                      accept={getAccept(item)}
                      multiple={false}
                      control={control}
                      errors={errors}
                      required
                    >
                      Select new file
                    </ControlledField>
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <ButtonLoading type="submit" appearance="primary" loading={isLoading} icon={<CheckmarkFilled />}>
                  {isDoc ? 'Complete version' : 'Create version'}
                </ButtonLoading>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">Cancel</Button>
                </DialogTrigger>
              </DialogActions>
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default CreateVersionDialog;
